import {
  type AssistantSourceGroup,
  groupAssistantSources,
} from './groupAssistantSources';
import {
  isInAppAssistantHref,
  toInAppAssistantLocation,
} from './inAppAssistantHref';
import type { AiAssistantMessage, AiAssistantSource } from './types';

type AiAssistantPageSource = Extract<AiAssistantSource, { kind: 'page' }>;

const MARKDOWN_LINK =
  /(?<!!)\[((?:\\.|[^\]])+)\]\((<[^>]+>|[^)\s]+)(?:\s+(?:"[^"]*"|'[^']*'))?\)/g;

function parseMarkdownHref(raw: string): string {
  if (raw.startsWith('<') && raw.endsWith('>')) {
    return raw.slice(1, -1);
  }
  return raw;
}

function isPageSource(
  source: AiAssistantSource
): source is AiAssistantPageSource {
  return source.kind === 'page';
}

/** In-app markdown links from a reply, deduped by location. */
export function extractReplyPageSources(text: string): AiAssistantPageSource[] {
  const seen = new Set<string>();
  const sources: AiAssistantPageSource[] = [];

  for (const match of text.matchAll(MARKDOWN_LINK)) {
    const label = match[1].replace(/\\(.)/g, '$1').trim();
    const href = parseMarkdownHref(match[2]);
    if (!isInAppAssistantHref(href)) {
      continue;
    }

    const to = toInAppAssistantLocation(href);
    if (seen.has(to)) {
      continue;
    }
    seen.add(to);
    sources.push({
      kind: 'page',
      label: label || to,
      to,
      fromReply: true,
    });
  }

  return sources;
}

/**
 * Single source of truth for a message's flat source list: API citations plus
 * in-app markdown reply links (deduped). Use this for the sources panel and
 * {@link resolveMessageSourceGroups} for badge/card grouping.
 */
export function resolveMessageSources(
  message: Pick<AiAssistantMessage, 'sources' | 'text'>
): AiAssistantSource[] {
  const existing = message.sources ?? [];
  const existingTos = new Set(
    existing
      .filter(isPageSource)
      .map((source) => toInAppAssistantLocation(source.to))
  );
  const replyPages = extractReplyPageSources(message.text).filter(
    (source) => !existingTos.has(source.to)
  );
  if (replyPages.length === 0) {
    return existing;
  }

  return [...existing, ...replyPages];
}

/** Grouped source cards for the message badge count and panel list. */
export function resolveMessageSourceGroups(
  message: Pick<AiAssistantMessage, 'sources' | 'text'>
): AssistantSourceGroup[] {
  return groupAssistantSources(resolveMessageSources(message));
}
